import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { sliderDeals, stores } from '../data/mockData';
import DealDialog from './DealDialog';

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const slides = sliderDeals;

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [slides.length, isTransitioning]);

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsTransitioning(false), 600);
  };

  const goToSlide = (index) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 600);
  };

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide, isPaused]);

  const handleDealClick = (slide) => {
    const store = stores.find(s => s.slug === slide.store);
    const dealData = {
      id: slide.id,
      title: slide.title,
      image: getSlideStyle(slide).image || 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=400&fit=crop',
      discount: slide.discount ? parseInt(slide.discount) : (slide.price ? 15 : 20),
      originalPrice: slide.price ? parseFloat(slide.price.replace('$', '')) * 1.2 : 99.99,
      salePrice: slide.price ? parseFloat(slide.price.replace('$', '')) : 79.99,
      couponCode: slide.couponCode,
      cashback: slide.cashback || `Save with code ${slide.couponCode}`,
      store: slide.store,
      storeUrl: slide.storeUrl,
      storeName: store?.name,
    };
    setSelectedDeal(dealData);
    setDialogOpen(true);
  };

  const getSlideStyle = (slide) => {
    const styles = {
      zara: {
        bg: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        image: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=800&h=500&fit=crop',
      },
      hm: {
        bg: 'linear-gradient(135deg, #E53A7F 0%, #C42D69 100%)',
        image: null,
      },
      nordstrom: {
        bg: 'linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)',
        image: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=800&h=500&fit=crop',
      },
      'savage-x-fenty': {
        bg: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
        image: 'https://images.unsplash.com/photo-1617331721458-bd3bd3f9c7f8?w=800&h=500&fit=crop',
      },
      shein: {
        bg: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)',
        image: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=800&h=500&fit=crop',
      },
      'victorias-secret': {
        bg: 'linear-gradient(135deg, #d4145a 0%, #b31048 100%)',
        image: 'https://images.unsplash.com/photo-1616244013536-d7c52fa73b96?w=800&h=500&fit=crop',
      },
    };
    return styles[slide.type] || styles.zara;
  };

  // Render slide for desktop (always visible)
  const renderDesktopSlide = (slide, position) => {
    const style = getSlideStyle(slide);
    const isMain = position === 0;

    if (slide.type === 'hm') {
      return (
        <div
          onClick={() => handleDealClick(slide)}
          className={`relative h-64 md:h-80 rounded-xl overflow-hidden flex items-center justify-center cursor-pointer transition-all duration-500 ${
            isMain ? 'scale-100 shadow-xl' : 'scale-[0.97] opacity-90'
          }`}
          style={{ background: style.bg }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="text-center text-white p-6 z-10">
            <div className="flex items-center justify-center mb-2">
              <span className="text-2xl md:text-3xl font-bold text-yellow-400">{slide.subtitle}</span>
              <span className="text-yellow-400 ml-2 text-xl">✦</span>
            </div>
            <h2 className="text-xl md:text-3xl font-bold mb-2">{slide.title}</h2>
            <p className="text-sm md:text-base mb-4 opacity-90">{slide.description}</p>
            <button className="bg-[#1a1a2e] text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-[#2a2a4e] transition-all duration-300 hover:scale-105">
              {slide.cashback}
            </button>
          </div>
          <div className="absolute bottom-3 left-3 bg-white/20 backdrop-blur-sm px-2 py-1 rounded text-white text-xs">
            Code: {slide.couponCode}
          </div>
        </div>
      );
    }

    return (
      <div
        onClick={() => handleDealClick(slide)}
        className={`relative h-64 md:h-80 rounded-xl overflow-hidden cursor-pointer transition-all duration-500 ${
          isMain ? 'scale-100 shadow-xl' : 'scale-[0.97] opacity-90'
        }`}
        style={{
          backgroundImage: style.image ? `${style.bg}, url(${style.image})` : style.bg,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="absolute inset-0 flex flex-col justify-center p-4 md:p-6">
          <p className="text-white/90 text-xs font-medium mb-1">{slide.subtitle}</p>
          <h2 className="text-white text-xl md:text-2xl font-bold mb-3">{slide.title}</h2>
          <button className="bg-white text-gray-800 px-4 py-2 rounded font-medium text-xs hover:bg-gray-100 transition-all duration-300 hover:scale-105 w-fit">
            {slide.buttonText || 'SEE DEAL'}
          </button>
        </div>
        <div className="absolute top-3 right-3 bg-[#E53A7F] text-white px-2 py-1 rounded font-bold text-xs">
          {slide.price || slide.discount}
        </div>
        <div className="absolute bottom-3 left-3 bg-white/20 backdrop-blur-sm px-2 py-1 rounded text-white text-xs">
          Code: {slide.couponCode}
        </div>
      </div>
    );
  };

  // Render slide for mobile (with animations)
  const renderMobileSlide = (slide, isActive) => {
    const style = getSlideStyle(slide);

    if (slide.type === 'walmart') {
      return (
        <div
          onClick={() => handleDealClick(slide)}
          className={`relative h-64 rounded-xl overflow-hidden flex items-center justify-center cursor-pointer transition-all duration-700 ${
            isActive ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}
          style={{ background: style.bg }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className={`text-center text-white p-6 z-10 transition-all duration-500 delay-200 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center justify-center mb-2">
              <span className="text-3xl font-bold text-yellow-400">{slide.subtitle}</span>
              <span className="text-yellow-400 ml-2 text-2xl">✦</span>
            </div>
            <h2 className="text-2xl font-bold mb-2">{slide.title}</h2>
            <p className="text-base mb-4 opacity-90">{slide.description}</p>
            <button className="bg-[#1a1a2e] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#2a2a4e] transition-colors">
              {slide.cashback}
            </button>
          </div>
          <div className="absolute bottom-4 left-4 bg-white/20 backdrop-blur-sm px-3 py-1 rounded text-white text-xs">
            Code: {slide.couponCode}
          </div>
        </div>
      );
    }

    return (
      <div
        onClick={() => handleDealClick(slide)}
        className={`relative h-64 rounded-xl overflow-hidden cursor-pointer transition-all duration-700 ${
          isActive ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
        }`}
        style={{
          backgroundImage: style.image ? `${style.bg}, url(${style.image})` : style.bg,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className={`absolute inset-0 flex flex-col justify-center p-6 transition-all duration-500 delay-200 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-white/90 text-xs font-medium mb-1">{slide.subtitle}</p>
          <h2 className="text-white text-2xl font-bold mb-4">{slide.title}</h2>
          <button className="bg-white text-gray-800 px-6 py-2 rounded font-medium text-sm hover:bg-gray-100 transition-colors w-fit">
            {slide.buttonText || 'SEE DEAL'}
          </button>
        </div>
        <div className={`absolute top-4 right-4 bg-[#E53A7F] text-white px-3 py-1 rounded font-bold text-sm transition-all duration-300 delay-300 ${isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
          {slide.price || slide.discount}
        </div>
        <div className={`absolute bottom-4 left-4 bg-white/20 backdrop-blur-sm px-3 py-1 rounded text-white text-xs transition-all duration-300 delay-400 ${isActive ? 'opacity-100' : 'opacity-0'}`}>
          Code: {slide.couponCode}
        </div>
      </div>
    );
  };

  // Get visible slides for desktop (3 at a time)
  const getVisibleSlides = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentSlide + i) % slides.length;
      visible.push({ slide: slides[index], position: i });
    }
    return visible;
  };

  return (
    <div className="relative px-4 py-6 bg-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Desktop: Show 3 slides side by side */}
        <div className="hidden lg:grid lg:grid-cols-3 gap-4">
          {getVisibleSlides().map(({ slide, position }, i) => (
            <div
              key={`desktop-${slide.id}-${i}`}
              className="transition-all duration-500 ease-out"
            >
              {renderDesktopSlide(slide, position)}
            </div>
          ))}
        </div>

        {/* Desktop Navigation Arrows */}
        <div className="hidden lg:block">
          <button
            onClick={prevSlide}
            className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-full shadow-lg hover:bg-white transition-all duration-300 z-20 hover:scale-110"
            disabled={isTransitioning}
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-full shadow-lg hover:bg-white transition-all duration-300 z-20 hover:scale-110"
            disabled={isTransitioning}
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Mobile: Single slide carousel */}
        <div className="lg:hidden relative">
          <div className="overflow-hidden">
            <div className="relative">
              {slides.map((slide, index) => (
                <div
                  key={`mobile-${slide.id}`}
                  className={`transition-all duration-700 ease-out ${
                    index === currentSlide 
                      ? 'relative' 
                      : 'absolute inset-0 pointer-events-none'
                  }`}
                >
                  {renderMobileSlide(slide, index === currentSlide)}
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-lg hover:bg-white transition-all duration-300 z-20 hover:scale-110"
            disabled={isTransitioning}
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-lg hover:bg-white transition-all duration-300 z-20 hover:scale-110"
            disabled={isTransitioning}
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Dots Navigation */}
        <div className="flex justify-center mt-4 space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`relative h-2 rounded-full transition-all duration-500 overflow-hidden ${
                index === currentSlide ? 'w-8 bg-[#E53A7F]' : 'w-2 bg-gray-300 hover:bg-gray-400'
              }`}
            >
              {index === currentSlide && !isPaused && (
                <span 
                  className="absolute inset-0 bg-[#C42D69] origin-left"
                  style={{
                    animation: 'progress 5s linear',
                  }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Progress animation keyframes */}
        <style>{`
          @keyframes progress {
            from { transform: scaleX(0); }
            to { transform: scaleX(1); }
          }
        `}</style>
      </div>

      {/* Deal Dialog */}
      <DealDialog
        deal={selectedDeal}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        storeName={selectedDeal?.storeName}
      />
    </div>
  );
};

export default HeroSlider;
